import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { APP_NAME } from '../config/brand'
import { AuthLayout } from '../layouts/AuthLayout'
import { IconEye, IconEyeOff } from '../components/icons'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { resetPassword } from '../services/authService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const CODE_RE = /^\d{6}$/
const RESET_EMAIL_STORAGE_KEY = 'shopora_business_admin_reset_email'

function resolveResetEmail(searchParams: URLSearchParams, location: ReturnType<typeof useLocation>): string {
  const fromState = (location.state as { email?: string } | null)?.email?.trim() ?? ''
  const fromStorage = sessionStorage.getItem(RESET_EMAIL_STORAGE_KEY)?.trim() ?? ''
  const fromQuery = searchParams.get('email')?.trim() ?? ''
  const email = fromState || fromStorage || fromQuery
  if (email && EMAIL_RE.test(email)) {
    sessionStorage.setItem(RESET_EMAIL_STORAGE_KEY, email)
    return email
  }
  return ''
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const email = resolveResetEmail(searchParams, location)

  useEffect(() => {
    if (searchParams.has('email') && email) {
      navigate('/reset-password', { replace: true, state: { email } })
    }
  }, [email, navigate, searchParams])

  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)

    const trimmedEmail = email.trim()
    if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
      setError('Start from the forgot password page to request a reset code.')
      return
    }
    if (!CODE_RE.test(code.trim())) {
      setError('Enter the 6-digit code from your email.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      await resetPassword({ email: trimmedEmail, code: code.trim(), newPassword: password })
      sessionStorage.removeItem(RESET_EMAIL_STORAGE_KEY)
      setDone(true)
    } catch (err) {
      setError(getUserFacingErrorMessage(err, 'Could not reset password.'))
    } finally {
      setSubmitting(false)
    }
  }

  const brandInitial = APP_NAME.charAt(0).toUpperCase()

  if (!email && !done) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md shadow-2xl">
          <div className="space-y-4 text-center">
            <h1 className="text-xl font-semibold text-foreground">Reset your password</h1>
            <p className="text-sm text-muted-foreground" role="alert">
              Request a reset code first, then return here to set your new password.
            </p>
            <p className="text-sm">
              <Link to="/forgot-password" className="font-medium text-primary underline-offset-4 hover:underline">
                Request reset code
              </Link>
              {' · '}
              <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md shadow-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-lg font-bold text-primary-foreground shadow-lg">
            {brandInitial}
          </div>
          <h1 className="text-xl font-semibold text-foreground">
            {done ? 'Password updated' : 'Set a new password'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {done
              ? 'You can sign in with your new password.'
              : 'Enter the 6-digit code from your email and choose a new password.'}
          </p>
        </div>

        {done ? (
          <p className="text-center text-sm">
            <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
              Go to sign in
            </Link>
          </p>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" noValidate>
            <Input
              id="reset-code"
              type="text"
              label="Reset code"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
            />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              label="New password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              endAdornment={
                <button
                  type="button"
                  className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <IconEyeOff className="h-[1.125rem] w-[1.125rem]" aria-hidden />
                  ) : (
                    <IconEye className="h-[1.125rem] w-[1.125rem]" aria-hidden />
                  )}
                </button>
              }
            />
            <Input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              label="Confirm password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
            {error ? (
              <p
                className="rounded-xl border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                role="alert"
              >
                {error}
              </p>
            ) : null}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Updating…' : 'Update password'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/forgot-password" className="font-medium text-primary underline-offset-4 hover:underline">
                Request a new code
              </Link>
            </p>
          </form>
        )}
      </Card>
    </AuthLayout>
  )
}
